import React, { useRef, useEffect, useState } from 'react';
import styles from './style.module.scss';
import map from '../../assets/images/map.png';

interface FaceHighlightProps {
  imageUrl: string;
  faceData?: {
    boundingBox: {
      xMin: number;
      yMin: number;
      xMax: number;
      yMax: number;
    };
    landmarks: Array<{
      type: string;
      x: number;
      y: number;
    }>;
  };
}

export const FaceHighlight: React.FC<FaceHighlightProps> = ({ imageUrl, faceData }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [imageDisplayRect, setImageDisplayRect] = useState({
    width: 0,
    height: 0,
    left: 0,
    top: 0,
  });

  const getImageDisplayRect = () => {
    if (!imageRef.current) return null;

    const rect = imageRef.current.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
      left: rect.left,
      top: rect.top,
    };
  };

  const transformLandmarkCoordinates = (
    landmarkX: number,
    landmarkY: number,
    imgNaturalWidth: number,
    imgNaturalHeight: number,
    displayWidth: number,
    displayHeight: number
  ) => {
    const containerAspect = displayWidth / displayHeight;
    const imageAspect = imgNaturalWidth / imgNaturalHeight;

    let drawWidth, drawHeight, offsetX, offsetY;

    if (containerAspect > imageAspect) {
      drawWidth = displayWidth;
      drawHeight = displayWidth / imageAspect;
      offsetX = 0;
      offsetY = (displayHeight - drawHeight) / 2;
    } else {
      drawHeight = displayHeight;
      drawWidth = displayHeight * imageAspect;
      offsetX = (displayWidth - drawWidth) / 2;
      offsetY = 0;
    }

    const scaleX = drawWidth / imgNaturalWidth;
    const scaleY = drawHeight / imgNaturalHeight;

    const x = landmarkX * scaleX + offsetX;
    const y = landmarkY * scaleY + offsetY;

    return { x, y };
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });

    // Получаем реальные размеры изображения на экране
    const rect = getImageDisplayRect();
    if (rect) {
      setImageDisplayRect(rect);
    }

    setImageLoaded(true);
  };

  // Обновляем размеры изображения при ресайзе
  useEffect(() => {
    const updateImageRect = () => {
      const rect = getImageDisplayRect();
      if (rect) {
        setImageDisplayRect(rect);
      }
    };

    window.addEventListener('resize', updateImageRect);
    setTimeout(updateImageRect, 100);

    return () => window.removeEventListener('resize', updateImageRect);
  }, [imageLoaded]);

  const drawFaceShape = (
    ctx: CanvasRenderingContext2D,
    landmarks: Array<{ type: string; x: number; y: number }>,
    imgNaturalWidth: number,
    imgNaturalHeight: number,
    displayWidth: number,
    displayHeight: number
  ) => {
    const eyebrowTypes = [
      'LEFT_OF_LEFT_EYEBROW',
      'RIGHT_OF_LEFT_EYEBROW',
      'LEFT_OF_RIGHT_EYEBROW',
      'RIGHT_OF_RIGHT_EYEBROW',
    ];

    const EYEBROW_OFFSET = -14; // поднимаем над бровями
    const SIDE_OFFSET = 3; // расширяем виски
    const CHIN_OFFSET = 6; // чуть округляем подбородок

    const targetPoints = [
      'LEFT_EAR_TRAGION',
      'LEFT_OF_LEFT_EYEBROW',
      'RIGHT_OF_LEFT_EYEBROW',
      'LEFT_OF_RIGHT_EYEBROW',
      'RIGHT_OF_RIGHT_EYEBROW',
      'RIGHT_EAR_TRAGION',
      'CHIN_RIGHT_GONION',
      'CHIN_GNATHION',
      'CHIN_LEFT_GONION',
    ];

    const points = targetPoints
      .map((type) => {
        const lm = landmarks.find((l) => l.type === type);
        if (!lm) return null;

        const p = transformLandmarkCoordinates(
          lm.x,
          lm.y,
          imgNaturalWidth,
          imgNaturalHeight,
          displayWidth,
          displayHeight
        );

        if (eyebrowTypes.includes(type)) p.y += EYEBROW_OFFSET;
        if (type.includes('EAR')) p.x += type.startsWith('LEFT') ? SIDE_OFFSET - 2 : SIDE_OFFSET;
        if (type.includes('CHIN')) p.y += CHIN_OFFSET;

        return { ...p, type };
      })
      .filter(Boolean);

    if (points.length < 3) return;

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const cx = (p0.x + p1.x) / 2;
      const cy = (p0.y + p1.y) / 2;
      ctx.quadraticCurveTo(p0.x, p0.y, cx, cy);
    }

    // замыкаем кривую
    ctx.quadraticCurveTo(
      points[points.length - 1].x,
      points[points.length - 1].y,
      points[0].x,
      points[0].y
    );

    ctx.strokeStyle = '#D9D9D9';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.closePath();
  };

  useEffect(() => {
    if (
      !canvasRef.current ||
      !imageLoaded ||
      !faceData ||
      imageDisplayRect.width === 0 ||
      imageDimensions.width === 0
    )
      return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    //@ts-ignore
    const scaleX = imageDimensions.width / faceData.originalWidth;
    //@ts-ignore
    const scaleY = imageDimensions.height / faceData.originalHeight;

    const correctedLandmarks = faceData.landmarks.map((l) => ({
      ...l,
      x: l.x * scaleX,
      y: l.y * scaleY,
    }));

    canvas.width = imageDisplayRect.width;
    canvas.height = imageDisplayRect.height;

    ctx.setTransform(1, 0, 0, 1, 0, 0);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const createFaceShapePath = (
      ctx: CanvasRenderingContext2D,
      landmarks: Array<{ type: string; x: number; y: number }>,
      imgNaturalWidth: number,
      imgNaturalHeight: number,
      displayWidth: number,
      displayHeight: number
    ) => {
      const eyebrowTypes = [
        'LEFT_OF_LEFT_EYEBROW',
        'RIGHT_OF_LEFT_EYEBROW',
        'LEFT_OF_RIGHT_EYEBROW',
        'RIGHT_OF_RIGHT_EYEBROW',
      ];

      const EYEBROW_OFFSET = -15;
      const SIDE_OFFSET = 8;
      const CHIN_OFFSET = 6;

      const targetPoints = [
        'LEFT_EAR_TRAGION',
        'LEFT_OF_LEFT_EYEBROW',
        'RIGHT_OF_LEFT_EYEBROW',
        'LEFT_OF_RIGHT_EYEBROW',
        'RIGHT_OF_RIGHT_EYEBROW',
        'RIGHT_EAR_TRAGION',
        'CHIN_RIGHT_GONION',
        'CHIN_GNATHION',
        'CHIN_LEFT_GONION',
      ];

      const points = targetPoints
        .map((type) => {
          const lm = landmarks.find((l) => l.type === type);
          if (!lm) return null;

          const p = transformLandmarkCoordinates(
            lm.x,
            lm.y,
            imgNaturalWidth,
            imgNaturalHeight,
            displayWidth,
            displayHeight
          );

          if (eyebrowTypes.includes(type)) p.y += EYEBROW_OFFSET;
          if (type.includes('EAR')) p.x += type.startsWith('LEFT') ? SIDE_OFFSET - 5 : SIDE_OFFSET;
          if (type.includes('CHIN')) p.y += CHIN_OFFSET;

          return p;
        })
        .filter(Boolean);

      if (points.length < 3) return false;

      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);

      for (let i = 1; i < points.length - 1; i++) {
        const p0 = points[i];
        const p1 = points[i + 1];
        const cx = (p0.x + p1.x) / 2;
        const cy = (p0.y + p1.y) / 2;
        ctx.quadraticCurveTo(p0.x, p0.y, cx, cy);
      }

      ctx.quadraticCurveTo(
        points[points.length - 1].x,
        points[points.length - 1].y,
        points[0].x,
        points[0].y
      );

      ctx.closePath();
      return true;
    };

    ctx.save();
    if (
      createFaceShapePath(
        ctx,
        correctedLandmarks,
        imageDimensions.width,
        imageDimensions.height,
        imageDisplayRect.width,
        imageDisplayRect.height
      )
    ) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fill();
    }
    ctx.restore();

    drawFaceShape(
      ctx,
      correctedLandmarks,
      imageDimensions.width,
      imageDimensions.height,
      imageDisplayRect.width,
      imageDisplayRect.height
    );
  }, [faceData, imageLoaded, imageDimensions, imageDisplayRect]);

  return (
    <div ref={containerRef} className={styles.userImageContainer} style={{ position: 'relative' }}>
      <img
        ref={imageRef}
        src={imageUrl}
        alt='user photo'
        onLoad={handleImageLoad}
        style={{
          width: 'calc((100vw - 48px) / 2)',
          height: '248px',
          objectFit: 'cover',
          borderRadius: '16px',
        }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          pointerEvents: 'none',
          borderRadius: '16px',
        }}
      />
      <img
        src={map}
        alt='map and dna stats'
        style={{
          width: 'calc((100vw - 48px) / 2)',
          height: '248px',
          objectFit: 'cover',
          borderRadius: '16px',
        }}
      />
    </div>
  );
};
